import { round } from 'es-toolkit';
import { RefCallback } from 'preact';
import { useLocation } from 'preact-iso';
import { useMemo } from 'preact/hooks';
import { usePodcastsStore } from '../../global-state/podcasts';
import { useIsAppDeveloper } from '../../global-state/user';
import { Episode } from '../../models/podcasts';
import getAdsStatus from '../../utils/ads-status';
import { trackedPriorities } from '../../utils/bump-episode-priority-when-in-view';
import { useIsPlayed } from '../../utils/check-if-played';
import { relativeDate } from '../../utils/format-date';
import getThumbUrl from '../../utils/get-thumb';
import { classNames, getPlayableError, isPlayable } from '../../utils/helpers';
import { useMergedRef } from '../../utils/merged-ref';
import { type SwipeAction, useSwipe } from '../../utils/swipe';
import DownloadButton from '../download-button';
import { Icon } from '../icon';
import { Link } from '../link';
import { ListenLaterButton } from '../listen-later-button';
import PlayButton from '../play-button';
import { PlayNextButton } from '../play-next-button';

export { SwipeAction };

export const EpisodeItem = ({
  episode,
  showThumbnail = true,
  swipeAction,
  createContentRef,
}: {
  episode: Episode;
  showThumbnail?: boolean;
  swipeAction?: SwipeAction;
  createContentRef?: (episode: Episode) => RefCallback<HTMLDivElement>;
}) => {
  const { title, guidHash, podcastId } = episode;
  const adsStatus = getAdsStatus(episode);
  const isPlayed = useIsPlayed(episode);
  const isGreyedOut = !isPlayable(episode) || isPlayed;
  const { route } = useLocation();

  const {
    swipeHandlers,
    swipeDismissStyles,
    handleSwipeContainerClick,
    getSwipeContainerStyles,
    getSwipeButtonStyles,
    swipeContainerRef,
  } = useSwipe(swipeAction);

  // Preact adds an ._unmount property to ref functions per element, so we need to create a new
  // instance of it for our component.
  const contentRefCallback = useMemo(
    () => (createContentRef ? createContentRef(episode) : undefined),
    [episode.id, createContentRef]
  );

  const showPriorityForDebugging = useIsAppDeveloper();

  // No need to subscribe to changes, thumbnails almost never change
  const podcastForThumbUrl = usePodcastsStore.getState().podcasts[episode.podcastId];

  return (
    // This outer wrapper is just used for dismiss animation
    <div style={swipeDismissStyles}>
      <article
        {...(swipeAction ? swipeHandlers : [])}
        // Need to expose ref both to my component and swipeable
        ref={useMergedRef(swipeContainerRef, swipeHandlers.ref)}
        onClick={swipeAction ? handleSwipeContainerClick : undefined}
        style={{ touchAction: 'pan-y', ...getSwipeContainerStyles() }}
        class="px-0 py-2"
      >
        {swipeAction && (
          <div
            class="absolute left-[calc(100%+24px)] flex h-full items-center justify-center overflow-hidden"
            style={getSwipeButtonStyles()}
          >
            {swipeAction.text}
          </div>
        )}
        <Link href={`/podcast/${podcastId}/episode/${guidHash}`} class="block">
          {showThumbnail ? (
            <img
              src={podcastForThumbUrl ? getThumbUrl(podcastForThumbUrl, 40) : ''}
              alt={episode.podcast.name}
              width="40"
              height="40"
              onClick={(e) => {
                // Go to podcast instead of episode when podcast thumbnail is clicked
                e.stopImmediatePropagation();
                e.preventDefault();
                route(`/podcast/${episode.podcastId}`);
              }}
              class={classNames(
                'float-left mb-0.5 mr-1.5 h-[39px] w-[39px] rounded-[4px] border border-[#e7e7e7]',
                '[@media(min-width:450px)]:h-[41px] [@media(min-width:450px)]:w-[41px]',
                '[@media(min-width:500px)]:h-[43px] [@media(min-width:500px)]:w-[43px]'
              )}
            />
          ) : (
            ''
          )}
          <div
            class={classNames(
              'text-[13px] [@media(min-width:450px)]:text-[14px] [@media(min-width:500px)]:text-[14.5px]',
              'text-gray-800',
              isGreyedOut && 'opacity-70'
            )}
          >
            <time dateTime={episode.pubDate.toISOString()}>
              {relativeDate(episode.pubDate, showThumbnail ? 'short' : 'default')}
            </time>
            <span>{adsStatus ? ` · ${adsStatus}` : ''}</span>
            {episode.omit && (
              <Icon
                sprite="rss-error"
                class="float-right inline-block h-5 w-5 text-amber-600"
              />
            )}
          </div>
          {/* Tailwind class ensures that title is well within viewport for intersection observer */}
          <div ref={contentRefCallback} class="-mt-0.5 pt-0.5">
            <h3
              class={classNames(
                'mb-0.5 text-pretty text-[16px] leading-[22px]',
                '[@media(min-width:450px)]:text-[17px] [@media(min-width:450px)]:leading-[23px]',
                '[@media(min-width:500px)]:text-[18px] [@media(min-width:500px)]:leading-[24px]',
                isGreyedOut && 'opacity-70'
              )}
            >
              {title}
              {showPriorityForDebugging && <PriorityIndicator episode={episode} />}
            </h3>
            <p
              class={classNames(
                'text-[13px] [@media(min-width:450px)]:text-[14px] [@media(min-width:500px)]:text-[14.5px]',
                'mb-1 line-clamp-3 whitespace-pre-wrap',
                isGreyedOut && 'opacity-70'
              )}
            >
              {episode.excerpt}
            </p>
          </div>
        </Link>
        <EpisodeButtons episode={episode} />
      </article>
    </div>
  );

  function PriorityIndicator({ episode }: { episode: Episode }) {
    const priority = trackedPriorities.value[episode.id];
    return (
      <span class="absolute right-2 rounded bg-white p-0.5 text-sm text-yellow-500">
        {priority ? round(priority, 1) : episode.ads ? '' : '?'}
      </span>
    );
  }
};

export const EpisodeButtons = ({ episode }: { episode: Episode }) => {
  return (
    <div
      // overflow hidden prevents re-paints of subsequent items (Chrome on Mac)
      class="flex gap-2.5 overflow-hidden"
      onClick={preventLinkClick}
    >
      {isPlayable(episode) ? (
        <>
          <PlayButton episode={episode} />
          <PlayNextButton episode={episode} />
          <ListenLaterButton episode={episode} />
          <DownloadButton episode={episode} />
        </>
      ) : (
        <p class="text-[13px] italic text-red-600 [@media(min-width:450px)]:text-[14px]">
          {getPlayableError(episode)}
        </p>
        /* TODO -> allow opening videos in new window or something */
      )}
    </div>
  );
};

function preventLinkClick(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}
